import * as React from "react";
import { BoardblendLoader } from "@/components/boardblend/boardblend-loader";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface LoadingButtonProps
  extends Omit<ButtonProps, "asChild" | "onClick"> {
  loading?: boolean;
  loadingLabel?: React.ReactNode;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
}

export const LoadingButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps
>(
  (
    {
      children,
      className,
      disabled,
      loading = false,
      loadingLabel,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [pending, setPending] = React.useState(false);
    const isLoading = loading || pending;

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      const result = onClick?.(event);
      if (!result) return;

      setPending(true);
      try {
        await result;
      } finally {
        setPending(false);
      }
    };

    return (
      <Button
        ref={ref}
        className={cn("relative", className)}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        onClick={handleClick}
        {...props}
      >
        <span
          className={cn("contents", isLoading && "invisible")}
          data-loading-content
        >
          {children}
        </span>
        {isLoading && (
          <span className="button-loading-indicator absolute flex items-center gap-2">
            <BoardblendLoader compact />
            {loadingLabel}
          </span>
        )}
      </Button>
    );
  },
);

LoadingButton.displayName = "LoadingButton";
